// parse.ts — split tool-UI markdown into a flat prose/component list.
//
// Mirrors app/harness/toolui.py. The harness parses the same string to
// decide which channels to open; this parse decides what to render. The
// two must agree on the grammar, so the rules are restated here rather
// than inferred: a component tag sits alone on its own line, is
// self-closing, and carries only quoted attributes or bare flags.
//
// Output is deliberately FLAT. Components are hoisted to top level so a
// UI replacement that only rewrites prose cannot change a component's
// position among its siblings — which is what would otherwise force React
// to unmount it and drop a terminal's scrollback. See LiveMarkdown.tsx.

export const COMPONENT_TAGS = ['text', 'progressbar', 'terminal', 'linegraph', 'controlpanel'] as const;
export type ComponentTag = (typeof COMPONENT_TAGS)[number];

// Tags that consume a channel. `text` is static; a UI built only from
// `text` has no socket and therefore cannot update itself.
const LIVE_TAGS = new Set<ComponentTag>(['progressbar', 'terminal', 'linegraph', 'controlpanel']);

const ALLOWED_ATTRS: Record<ComponentTag, Set<string>> = {
    text: new Set(['id', 'value', 'markdown', 'latex']),
    progressbar: new Set(['id', 'channel', 'label', 'value', 'max']),
    terminal: new Set(['id', 'channel', 'markdown', 'latex', 'rows']),
    linegraph: new Set(['id', 'channel', 'label', 'xlabel', 'ylabel']),
    controlpanel: new Set(['id', 'channel', 'label', 'buttons']),
};

export interface SeedButton {
    id: string;
    label: string;
    variant?: string;
}

/**
 * Parse a `controlpanel` seed: `id=Label|variant;id2=Label2`.
 *
 * A panel whose buttons only ever arrive by frame cannot render an
 * approve/deny prompt: there is nothing to click that would cause the
 * frame. The seed is what makes the first paint useful. Frames still
 * override it, which is how a server disables a button once pressed.
 */
export function parseSeedButtons(raw: unknown): SeedButton[] {
    if (typeof raw !== 'string' || !raw.trim()) return [];
    const out: SeedButton[] = [];
    for (const entry of raw.split(';')) {
        const [idPart, rest] = splitOnce(entry.trim(), '=');
        const id = idPart.trim();
        if (!id || !IDENT_RE.test(id)) continue;
        const [labelPart, variant] = splitOnce(rest, '|');
        const label = labelPart.trim() || id;
        out.push({
            id,
            label,
            variant: variant.trim() === 'primary' ? 'primary' : undefined,
        });
    }
    return out;
}

function splitOnce(s: string, sep: string): [string, string] {
    const i = s.indexOf(sep);
    return i === -1 ? [s, ''] : [s.slice(0, i), s.slice(i + sep.length)];
}

const FLAG_ATTRS = new Set(['markdown', 'latex']);
const INT_ATTRS = new Set(['value', 'max', 'rows']);

// Matches the Python ceiling. A server that emits thousands of components
// would otherwise open thousands of sockets.
export const MAX_COMPONENTS = 32;

const IDENT_RE = /^[A-Za-z0-9_.:-]{1,64}$/;
const TAG_LINE_RE = /^[ \t]*<([A-Za-z][A-Za-z0-9_-]*)([^<>]*?)\/>[ \t]*$/;
const ATTR_RE = /([A-Za-z_][A-Za-z0-9_-]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'))?/g;

export interface ProseNode {
    kind: 'prose';
    markdown: string;
}

export interface ComponentNode {
    kind: 'component';
    tag: ComponentTag;
    /** Stable React key and channel-store key. Never empty. */
    id: string;
    /** Empty for static components, or when the server sent a bad name. */
    channel: string;
    attrs: Record<string, string | number | boolean>;
}

export type Node = ProseNode | ComponentNode;

export interface ParsedUI {
    nodes: Node[];
    channels: string[];
}

export function parseToolUI(markdown: string): ParsedUI {
    if (!markdown || !markdown.trim()) return { nodes: [], channels: [] };

    const nodes: Node[] = [];
    const channels: string[] = [];
    const seenIds = new Set<string>();
    let prose: string[] = [];
    let componentCount = 0;

    const flushProse = () => {
        if (!prose.length) return;
        const text = prose.join('\n').replace(/^\n+|\n+$/g, '');
        prose = [];
        if (text.trim()) nodes.push({ kind: 'prose', markdown: text });
    };

    for (const line of markdown.split('\n')) {
        const match = TAG_LINE_RE.exec(line);
        const tag = match?.[1]?.toLowerCase();
        if (!match || !tag || !(COMPONENT_TAGS as readonly string[]).includes(tag)) {
            prose.push(line);
            continue;
        }
        if (componentCount >= MAX_COMPONENTS) {
            // Keep it as text so there is something where the component
            // would have been, rather than a silent gap.
            prose.push(line);
            continue;
        }
        const node = buildComponent(tag as ComponentTag, match[2] ?? '', componentCount, seenIds);
        flushProse();
        nodes.push(node);
        seenIds.add(node.id);
        componentCount += 1;
        if (node.channel && !channels.includes(node.channel)) channels.push(node.channel);
    }
    flushProse();
    return { nodes, channels };
}

function buildComponent(
    tag: ComponentTag,
    rawAttrs: string,
    ordinal: number,
    seenIds: Set<string>,
): ComponentNode {
    const parsed = parseAttrs(rawAttrs);
    const allowed = ALLOWED_ATTRS[tag];
    const attrs: Record<string, string | number | boolean> = {};

    for (const [key, value] of Object.entries(parsed)) {
        if (!allowed.has(key) || key === 'id' || key === 'channel') continue;
        const coerced = coerce(key, value);
        if (coerced === undefined) continue;
        attrs[key] = coerced;
    }

    let id = typeof parsed.id === 'string' ? parsed.id : '';
    if (!IDENT_RE.test(id) || seenIds.has(id)) id = `${tag}-${ordinal}`;

    let channel = typeof parsed.channel === 'string' ? parsed.channel : '';
    if (!LIVE_TAGS.has(tag) || !IDENT_RE.test(channel)) channel = '';

    return { kind: 'component', tag, id, channel, attrs };
}

function parseAttrs(raw: string): Record<string, string | boolean> {
    const out: Record<string, string | boolean> = {};
    ATTR_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = ATTR_RE.exec(raw)) !== null) {
        const key = m[1];
        const quoted = m[2] ?? m[3];
        if (quoted === undefined) {
            // Bare word. Only the known flags mean anything; anything else
            // is a typo or a value that lost its quotes, and guessing
            // which would be worse than dropping it.
            if (FLAG_ATTRS.has(key)) out[key] = true;
            continue;
        }
        out[key] = quoted;
    }
    return out;
}

function coerce(key: string, value: string | boolean): string | number | boolean | undefined {
    if (FLAG_ATTRS.has(key)) {
        if (typeof value === 'boolean') return value;
        return !['', 'false', '0', 'no'].includes(value.trim().toLowerCase());
    }
    if (INT_ATTRS.has(key)) {
        const n = Number.parseInt(String(value).trim(), 10);
        return Number.isFinite(n) ? n : undefined;
    }
    return value;
}
